<template>
    <div class="FontSelector">
        <ul class="FontBuilder__selector">
            <li
                v-for="(font, index) in pairings"
                :key="index"
                class="Font"
            >
                <input
                    :id="'pairing' + index"
                    name="pairing[]"
                    class="Font__input"
                    type="radio"
                    :value="font.id"
                    v-model="pairingId"
                    @change="onFontSelect()"
                >
                <label
                    :for="'pairing' + index"
                    class="Font__label"
                >
                    <span
                        class="Font__label__heading"
                        :style="generateFontSwatchCSS(font.pairing.h1)"
                    >
                        {{ font.pairing.h1.family }}
                    </span>
                    <span
                        class="Font__label__body"
                        :style="generateFontSwatchCSS(font.pairing.body)"
                    >
                        {{ font.pairing.body.family }}
                    </span>
                </label>
                <span class="FontSelector__selected">
                    <i class="fas fa-check-circle" />
                </span>
            </li>
        </ul>

        <div class="FontBuilder__custom">
            <div class="FontBuilder__custom__title">
                <h3 class="FontBuilder__custom__title">
                    Selected Fonts
                </h3>
                <HelpModal
                    v-if="helpText"
                    :text="helpText"
                    :title="helpTitle"
                />
            </div>
            <p
                class="FontBuilder__upsell"
                v-if="role !== customFontAccessLevel"
                v-html="customFontUpsell"
            />
            <div class="FontBuilder__custom__fonts">
                <div class="FontBuilder__custom__font">
                    <div
                        class="FontBuilder__custom__font__input"
                        v-if="role !== customFontAccessLevel"
                    >
                        <v-text-field
                            label="Heading Font"
                            outlined
                            v-model="font1"
                            class="FontBuilder__custom__font__ro jsReadOnly"
                            :readonly="true"
                            aria-readonly="true"
                        />
                    </div>
                    <div
                        class="FontBuilder__custom__font__input"
                        v-else
                    >
                        <v-autocomplete
                            :items="fonts"
                            item-text="family"
                            item-value="family"
                            label="Heading Font"
                            v-model="font1"
                            @change="onChangeCustom()"
                            outlined
                            class="FontBuilder__custom__font__font"
                            :readonly="role !== customFontAccessLevel"
                            :aria-readonly="role !== customFontAccessLevel"
                            :class="{jsReadOnly : role !== customFontAccessLevel}"
                        />
                        <v-text-field
                            label="Heading Size"
                            type="number"
                            outlined
                            @change="onChangeCustom()"
                            v-model="font1Size"
                            class="FontBuilder__custom__font__size"
                        />
                    </div>
                </div>

                <div class="FontBuilder__custom__font">
                    <div
                        class="FontBuilder__custom__font__input"
                        v-if="role !== customFontAccessLevel"
                    >
                        <v-text-field
                            label="Body Font"
                            outlined
                            v-model="font2"
                            class="FontBuilder__custom__font__ro jsReadOnly"
                            :readonly="true"
                            aria-readonly="true"
                        />
                    </div>
                    <div
                        class="FontBuilder__custom__font__input"
                        v-else
                    >
                        <v-autocomplete
                            :items="fonts"
                            item-text="family"
                            item-value="family"
                            label="Body Font"
                            v-model="font2"
                            @change="onChangeCustom()"
                            outlined
                            class="FontBuilder__custom__font__font"
                        />
                        <v-text-field
                            label="Body Size"
                            type="number"
                            outlined
                            @change="onChangeCustom()"
                            v-model="font2Size"
                            class="FontBuilder__custom__font__size"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import SubmissionBoilerplate from '@/views/Public/Field/Fields/SubmissionBoilerplate';
import store from '@/store/store';
import { EventBus } from '@/store/eventBus';
import { mapActions, mapMutations } from 'vuex';
import getFontsByPairingId from '@/store/Modules/lib/getFontsByPairingId';
import setWeight from '@/store/Modules/lib/setWeight';
import setStyle from '@/store/Modules/lib/setStyle';
export default {
    mixins: [SubmissionBoilerplate],

    props: {
        cardNamespace: {
            type: String,
            required: false,
        }
    },

    data() {
        return {
            font: [],
        };
    },

    computed: {
        role() {
            return store.state.auth.user.role;
        },

        fonts() {
            return store.state.websiteDesign.fonts;
        },

        pairingId: {
            get() {
                return store.state.websiteDesign.pairingId;
            },

            set(value) {
                this.setPairingId(value);
            }
        },

        pairings: {
            get() {
                return store.state.websiteDesign.pairings;
            }
        },

        font1: {
            get() {
                return store.state.websiteDesign.font1;
            },

            set(value) {
                this.setFont1(value);
            }
        },

        font1Size: {
            get() {
                return store.state.websiteDesign.font1Size;
            },

            set(value) {
                this.setFont1Size(value);
            }
        },


        font2: {
            get() {
                return store.state.websiteDesign.font2;
            },

            set(value) {
                this.setFont2(value);
            }
        },

        font2Size: {
            get() {
                return store.state.websiteDesign.font2Size;
            },

            set(value) {
                this.setFont2Size(value);
            }
        },

        customFontUpsell() {
            return store.state[this.fieldNamespace].options.customFontUpsell;
        },

        customFontAccessLevel() {
            return store.state[this.fieldNamespace].options.customFontAccessLevel;
        }
    },

    methods: {
        ...mapMutations({
            setPairingId: 'websiteDesign/setPairingId',
            setFont1: 'websiteDesign/setFont1',
            setFont1Size: 'websiteDesign/setFont1Size',
            setFont2: 'websiteDesign/setFont2',
            setFont2Size: 'websiteDesign/setFont2Size',
            setFontSwatchStyle: 'websiteDesign/setFontSwatchStyle'
        }),

        ...mapActions({
            getAllPairings: 'websiteDesign/getAllPairings',
            loadFontSelectors: 'websiteDesign/loadFontSelectors',
        }),

        loadFieldData() {
            this.populateFontAutocomplete().then(() => {
                this.getAllPairings().then(() => {
                    this.loadSelected()
                        .then(() => {
                            this.loadCustom()
                                .then((response) => {
                                    this.setFieldData(response);
                                    this.loadWebFonts();
                                })
                                .catch(() => {
                                    this.setDefaults()
                                        .then(() => {
                                            this.setFieldData();
                                            this.loadWebFonts();
                                        })
                                        .catch((id) => { // First pairing ID in this.pairings.
                                            this.setPairingId(id);
                                            this.onFontSelect();
                                            this.loadWebFonts();
                                        });
                                });
                        });
                });
            });
        },

        setDefaults() {
            return new Promise((resolve, reject) => {
                if (!this.pairingId &&
                    !this.font1 &&
                    !this.font2
                ) {
                    reject(this.pairings[0].id);
                }
                resolve();
            });
        },

        populateFontAutocomplete() {
            return this.loadFontSelectors();
        },

        loadWebFonts() {
            let googleFonts = [];

            this.pairings.forEach(pairing => {
                const webfonts = Object.values(pairing.pairing);
                webfonts.forEach(font => {
                    googleFonts.push(font.family + ':' + font.variant);
                });
            });

            window.WebFont.load({
                google: {
                    families: googleFonts
                }
            });
        },

        loadSelected() {
            return new Promise((resolve) => {
                if(this.submission && this.submission.pairingId) {
                    this.setPairingId(this.submission.pairingId);
                    resolve();
                    return;
                }

                resolve();
            });
        },

        loadCustom() {
            return new Promise((resolve, reject) => {
                // Look for custom.
                if (this.submission && this.submission.customPairing) {
                    this.setFont1(this.submission.customPairing.font_1);
                    this.setFont1Size(this.submission.customPairing.font_1_size);
                    this.setFont2(this.submission.customPairing.font_2);
                    this.setFont2Size(this.submission.customPairing.font_2_size);
                    resolve({'customFont': true});
                    return;
                }

                // Set Defaults.
                if (this.submission && this.submission.pairingId) {
                    const pairing = getFontsByPairingId(this.submission.pairingId, this.pairings);
                    this.setFont1(pairing.pairing.h1.family);
                    this.setFont1Size(pairing.pairing.h1.preview_size);
                    this.setFont2(pairing.pairing.body.family);
                    this.setFont2Size(pairing.pairing.body.preview_size);
                    resolve({'customFont': false});
                    return;
                }

                reject();
            });
        },

        onFontSelect() {
            if (this.pairingId) {
                const pairing = getFontsByPairingId(this.pairingId, this.pairings);
                this.setFont1(pairing.pairing.h1.family);
                this.setFont1Size(pairing.pairing.h1.preview_size);
                this.setFont2(pairing.pairing.body.family);
                this.setFont2Size(pairing.pairing.body.preview_size);
                this.setFieldData({'customFont': false});
            }
        },

        onChangeCustom() {
            this.setFont1(this.font1);
            this.setFont1Size(this.font1Size);
            this.setFont2(this.font2);
            this.setFont2Size(this.font2Size);
            this.setPairingId(null);
            this.setFieldData({'customFont': true});
        },

        setFieldData(args) {
            this.fieldData = {
                pairingId: this.pairingId,
                customPairing: {
                    font_1: this.font1,
                    font_1_size: this.font1Size,
                    font_2: this.font2,
                    font_2_size: this.font2Size,
                }
            };

            this.storeFieldData();
            EventBus.$emit('UserPreviewChange', args);
        },

        generateFontSwatchCSS(font) {
            const family = font.family;
            const familyVariant = font.variant ? font.variant : false;
            const weight = setWeight(familyVariant);
            const italic = setStyle(familyVariant);

            if (!family) {
                return;
            }

            let style = '';
            style += 'font-family:' + family + ';';
            style += 'font-weight:' + weight + ';';
            style += 'font-style:' + italic + ';';
            style += 'font-size:' + font.selector_size + 'px;';

            if (font.all_caps) {
                style += 'text-transform:' + 'uppercase' + ';';
            }

            return style;
        },
    }
};
</script>
<style lang="scss">
.FontBuilder__selector {
    list-style: none;
    margin: 0 0 24px !important;
    padding: 0 !important;

    display: grid;
    grid-gap: 1rem;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
}

.Font {
    position: relative;
    height: 100px;
    margin-right: 2px;
    margin-bottom: 2px;
    border-radius: $border-radius-root;
    border: 1px solid currentColor;
}

.Font__label {
    position: absolute;
    z-index: 2;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    flex-flow: row wrap;
    padding: 12px;
    border-radius: $border-radius-root;
    cursor: pointer;
    background-color: #ffffff;
}

.Font__label__heading {
    flex: 0 0 100%;
    width: 100%;
}

.Font__label__body {
    flex: 0 0 100%;
    width: 100%;
}

.Font__input {
    position: absolute;
    z-index: 0;
    cursor: pointer;
}

.FontSelector__selected {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 3;
    display: flex;
    flex: 0 0 100%;
    width: 100%;
    justify-content: center;
    align-content: center;
    align-items: center;
    border-radius: $border-radius-root;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s linear;

    i {
        position: absolute;
        top: 4px;
        right: 4px;
        color: $primary-500;
        font-size: 1rem;
        text-shadow: 2px 2px 2px rgba(black, 0.2);
    }
}

.Font__input:checked+label {
    & ~ .FontSelector__selected {
        opacity: 1;
        transition: opacity 0.2s linear;
    }
}

.FontBuilder__custom__title {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    flex: 0 0 100%;
    width: 100%;

    h3 {
        flex: 0 0 auto;
        width: auto;
    }
}

.FontBuilder__upsell {
    margin-bottom: 0 !important;
    font-size: 0.9rem;
    font-style: italic;
}

.FontBuilder__custom__fonts {
    display: flex;
    justify-content: space-between;
    flex-flow: row wrap;
    flex: 0 0 100%;
}

.FontBuilder__custom__font {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    flex: 0 0 100%;
    width: 100%;
    margin-top: 24px !important;

    @media (min-width: 768px) {
        flex: 0 0 48%;
        width: 48%;
    }
}

.FontBuilder__custom__font__input {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    flex: 0 0 100%;
    width: 100%;
    height: 100%;

    .jsReadOnly {
        .v-input__slot {
            background-color: #eaeaea;
        }
    }
}

.FontBuilder__custom__font__ro {
    flex: 0 0 100%;
    width: 100%;
}

.FontBuilder__custom__font__font {
    flex: 0 0 70%;
    width: 70%;
}

.FontBuilder__custom__font__size {
    flex: 0 0 28%;
    width: 28%;
}

</style>
